/// <reference types="vite/client" />

import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  Stack,
  IconButton,
  Card,
  CardContent,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import { LuBrain } from 'react-icons/lu';
import { RiMoonClearFill } from "react-icons/ri";
import { FaBook } from "react-icons/fa";
import { MdAnalytics } from "react-icons/md";
import { MdArrowForward, MdArrowBack } from "react-icons/md";
import { IoMdImages } from "react-icons/io";
import { loadStripe } from '@stripe/stripe-js';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth, AuthContextType } from '../contexts/AuthContext';
import { useLoginModal } from '../contexts/LoginModalContext';
import { useLocation } from 'react-router-dom';
import StarIcon from '@mui/icons-material/Star';
import SEO from '../components/SEO/SEO';
import StarrySky from '../components/StarrySky';

type SlideDirection = 'next' | 'prev';

interface ImportMetaEnv {
  readonly VITE_STRIPE_PUBLIC_KEY: string
  readonly VITE_API_URL: string
}

interface ImportMeta {
  readonly env: ImportMetaEnv
}

interface Testimonial {
  name: string;
  comment: string;
  rating: number;
}

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY || '');

export default function Home() {
  const { t } = useTranslation();
  const { currentUser } = useAuth() as AuthContextType;
  const { openLoginModal } = useLoginModal();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [currentStatImageIndex, setCurrentStatImageIndex] = useState(0);
  const [currentDreamImageIndex, setCurrentDreamImageIndex] = useState(0);
  const [currentNewDreamImageIndex, setCurrentNewDreamImageIndex] = useState(0);
  const [currentGalleryImageIndex, setCurrentGalleryImageIndex] = useState(0);
  const [statSlideDirection, setStatSlideDirection] = useState<SlideDirection>('next');
  const [dreamSlideDirection, setDreamSlideDirection] = useState<SlideDirection>('next');
  const [newDreamSlideDirection, setNewDreamSlideDirection] = useState<SlideDirection>('next');
  const [gallerySlideDirection, setGallerySlideDirection] = useState<SlideDirection>('next');
  const [showLoginWarning, setShowLoginWarning] = useState(false);

  const statImages = [
    '/assets/home/s-1.jpg',
    '/assets/home/s-2.jpg',
    '/assets/home/s-3.jpg'
  ];

  const dreamImages = [
    '/assets/home/d-1.jpg',
    '/assets/home/d-2.jpg',
    '/assets/home/d-3.jpg',
    '/assets/home/d-4.jpg'
  ];

  const newDreamImages = [
    '/assets/home/a-1.jpg',
    '/assets/home/a-2.jpg',
    '/assets/home/a-3.jpg'
  ];

  const dreamGalleryImages = [
    '/assets/home/b-1.jpg',
    '/assets/home/b-2.jpg',
    '/assets/home/b-3.jpg'
  ];

  const nextStatImage = () => {
    setStatSlideDirection('next');
    setCurrentStatImageIndex((prev) => (prev + 1) % statImages.length);
  };

  const prevStatImage = () => {
    setStatSlideDirection('prev');
    setCurrentStatImageIndex((prev) => (prev - 1 + statImages.length) % statImages.length);
  };

  const nextDreamImage = () => {
    setDreamSlideDirection('next');
    setCurrentDreamImageIndex((prev) => (prev + 1) % dreamImages.length);
  };

  const prevDreamImage = () => {
    setDreamSlideDirection('prev');
    setCurrentDreamImageIndex((prev) => (prev - 1 + dreamImages.length) % dreamImages.length);
  };

  const nextNewDreamImage = () => {
    setNewDreamSlideDirection('next');
    setCurrentNewDreamImageIndex((prev) => (prev + 1) % newDreamImages.length);
  };

  const prevNewDreamImage = () => {
    setNewDreamSlideDirection('prev');
    setCurrentNewDreamImageIndex((prev) => (prev - 1 + newDreamImages.length) % newDreamImages.length);
  };

  const nextGalleryImage = () => {
    setGallerySlideDirection('next');
    setCurrentGalleryImageIndex((prev) => (prev + 1) % dreamGalleryImages.length);
  };

  const prevGalleryImage = () => {
    setGallerySlideDirection('prev');
    setCurrentGalleryImageIndex((prev) => (prev - 1 + dreamGalleryImages.length) % dreamGalleryImages.length);
  };

  const slideAnimationStyles: Record<SlideDirection, SxProps<Theme>> = {
    next: {
      animation: 'slideInFromRight 0.6s ease',
      '@keyframes slideInFromRight': {
        '0%': {
          transform: 'translateX(40%)',
          opacity: 0
        },
        '100%': {
          transform: 'translateX(0)',
          opacity: 1
        }
      }
    },
    prev: {
      animation: 'slideInFromLeft 0.6s ease',
      '@keyframes slideInFromLeft': {
        '0%': {
          transform: 'translateX(-40%)',
          opacity: 0
        },
        '100%': {
          transform: 'translateX(0)',
          opacity: 1
        }
      }
    }
  };

  const sharedCarouselImageSx: SxProps<Theme> = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center',
    borderRadius: 'inherit'
  };

  useEffect(() => {
    const loadComments = async () => {
      try {
        const settingsRef = doc(db, 'settings', 'homeComments');
        const docSnap = await getDoc(settingsRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          const comments: Testimonial[] = [
            { 
              name: data.comment1?.author || '', 
              comment: data.comment1?.text || '', 
              rating: data.comment1?.rating || 0 
            },
            { 
              name: data.comment2?.author || '', 
              comment: data.comment2?.text || '', 
              rating: data.comment2?.rating || 0 
            },
            { 
              name: data.comment3?.author || '', 
              comment: data.comment3?.text || '', 
              rating: data.comment3?.rating || 0 
            }
          ];
          setTestimonials(comments);
        }
      } catch (error) {
        console.error("Error loading comments:", error);
      }
    };

    loadComments();
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const section = searchParams.get('section');
    
    if (section === 'pricing') {
      const pricingSection = document.getElementById('pricing-section');
      if (pricingSection) {
        const navbarHeight = 80; // Hauteur approximative de la navbar + padding
        const offset = pricingSection.getBoundingClientRect().top + window.pageYOffset - navbarHeight;
        window.scrollTo({ top: offset, behavior: 'smooth' });
      }
    }
  }, [location]);

  const features = [
    {
      icon: <RiMoonClearFill style={{ fontSize: '2rem', color: '#68f1d1' }} />,
      title: t('home.features.newDream.title'),
      description: t('home.features.newDream.description'),
      key: 'newDream'
    },
    {
      icon: <FaBook style={{ fontSize: '2rem', color: '#68f1d1' }} />,
      title: t('home.features.dreamJournal.title'),
      description: t('home.features.dreamJournal.description'),
      key: 'dreamJournal'
    },
    {
      icon: <IoMdImages style={{ fontSize: '2rem', color: '#68f1d1' }} />,
      title: t('home.features.gallery.title'),
      description: t('home.features.gallery.description'),
      key: 'gallery'
    },
    {
      icon: <MdAnalytics style={{ fontSize: '2rem', color: '#68f1d1' }} />,
      title: t('home.features.statistics.title'),
      description: t('home.features.statistics.description'),
      key: 'statistics'
    }
  ];

  const handleSubscription = async (priceId: string) => {
    if (!currentUser) {
      setSelectedPriceId(priceId);
      setShowLoginWarning(true);
      return;
    }

    try {
      console.log('Stripe Public Key:', import.meta.env.VITE_STRIPE_PUBLIC_KEY);
      console.log('API URL:', import.meta.env.VITE_API_URL);
      
      const stripe = await stripePromise;
      if (!stripe) throw new Error('Stripe not loaded');
      console.log('Stripe loaded successfully');

      const requestBody = {
        priceId,
        userId: currentUser.uid,
        userEmail: currentUser.email
      };
      console.log('Request body:', requestBody);

      const response = await fetch(`${import.meta.env.VITE_API_URL}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      console.log('Response status:', response.status);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        throw new Error('Erreur lors de la création de la session');
      }

      const sessionData = await response.json();
      console.log('Session data:', sessionData);
      
      const { error } = await stripe.redirectToCheckout({ sessionId: sessionData.sessionId });
      
      if (error) {
        console.error('Erreur Stripe:', error.message);
      }
    } catch (error) {
      console.error('Erreur:', error instanceof Error ? error.message : 'Erreur inconnue');
    }
  };

  const handlePricingButtonClick = (priceId: string | null) => {
    if (!currentUser) {
      if (priceId) {
        setSelectedPriceId(priceId);
        setShowLoginWarning(true);
      } else {
        setShowLoginWarning(true);
      }
      return;
    }

    navigate('/profile?openPlanModal=true');
  };

  useEffect(() => {
    if (currentUser && selectedPriceId) {
      handleSubscription(selectedPriceId);
      setSelectedPriceId(null);
    }
  }, [currentUser, selectedPriceId]);

  return (
    <>
      <SEO 
        title={t('seo.home.title', 'LunaNüva | Analyse et interprétation de rêves')}
        description={t('seo.home.description', 'Découvrez le sens caché de vos rêves grâce à notre application d\'analyse basée sur les approches psychologiques reconnues.')}
        keywords={t('seo.home.keywords', 'analyse rêves, interprétation rêves, journal des rêves, psychologie')}
      />
      <Box sx={{ 
        minHeight: '100vh', 
        position: 'relative', 
        background: {
          xs: 'var(--home-background2)',
          sm: 'var(--home-background3)',
          md: 'var(--home-background)',
        }
      }}>
      {/* <StarrySky /> */}
      
      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ pt: { xs: 8, sm: 12 }, pb: 6, position: 'relative' }}>
        <Box sx={{ textAlign: 'center', mb: 7, mt: 1, mr:{xs: 0, sm: 0, md: 11}, ml:{xs: 0, sm: 0, md: 11} }}>
          <Box 
            component="img"
            src="/assets/home/logo3-mid.png"
            alt="LunaNuva Logo"
            sx={{
              maxWidth: { xs: '280px', sm: '400px', md: '500px' },
              width: '100%',
              height: 'auto',
              mb: 2,
              display: 'block',
              margin: '0 auto'
            }}
          />
          {/* Titre de la section */}
          <Typography 
            variant="body1" 
            sx={{ 
              textAlign: 'center',
              color: 'var(--light-green)',
              mb: 2,
              mt: 7,
              fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.7rem' },
              fontFamily: 'Comfortaa'
            }}
          >
            {t('home.presentationTitle')}
          </Typography>
          
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: { xs: 3, sm: 4 },
            maxWidth: '1000px',
            margin: '0 auto'
          }}>
            {/* Colonne gauche */}
            <Box 
              sx={{ 
                display: { xs: 'block', sm: 'contents' },
                background: { xs: 'transparent', sm: 'transparent' },
                backdropFilter: { xs: 'blur(10px)', sm: 'none' },
                borderRadius: { xs: '26px', sm: '0' },
                p: { xs: 2, sm: 0 },
                mb: { xs: -1, sm: 0 }
              }}
            >
              <Typography 
                variant="body1" 
                sx={{ 
                  fontSize: { xs: '0.8rem', sm: '1rem', md: '1rem' },
                  color: { xs: 'white', sm: 'var(--primary-color)' },
                  fontFamily: 'Comfortaa',
                  fontWeight: { xs: 'normal', sm: 'normal' },
                  lineHeight: 1.6,
                  textAlign: 'left'
                }}
              >
                {t('home.presentationLeft')}
              </Typography>
            </Box>
            
            {/* Bouton Essayez - Mobile seulement (entre les deux textes) */}
            <Box 
              sx={{ 
                display: { xs: 'flex', sm: 'none' },
                justifyContent: 'center',
                mt: 1,
                mb: 0
              }}
            >
              <Button
                variant="contained"
                sx={{
                  background: 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '26px',
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontFamily: 'Comfortaa',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  boxShadow: '0 4px 15px rgba(13, 13, 13, 0.3)',
                  '&:hover': {
                    background: 'var(--secondary-color)',
                    transform: 'translateY(-2px)',
                    //boxShadow: '0 6px 20px rgba(0, 0, 0, 0.4)',
                    border: 'none'
                  },
                  transition: 'all 0.3s ease'
                }}
                onClick={() => {
                  const pricingSection = document.getElementById('pricing-section');
                  if (pricingSection) {
                    // Détection mobile
                    const isMobile = window.innerWidth < 600;
                    if (isMobile) {
                      // Scroll avec offset pour éviter la navbar
                      const yOffset = -80; // Ajustement pour la navbar mobile
                      const y = pricingSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
                      window.scrollTo({ top: y, behavior: 'smooth' });
                    } else {
                      pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }
                }}
              >
                {t('home.tryButton')}
              </Button>
            </Box>
            
            {/* Colonne droite */}
            <Box 
              sx={{ 
                mt: -2,
                display: { xs: 'block', sm: 'contents' },
                background: { xs: 'transparent', sm: 'transparent' },
                backdropFilter: { xs: 'blur(10px)', sm: 'none' },
                borderRadius: { xs: '26px', sm: '0' },
                p: { xs: 2, sm: 0 }
              }}
            >
              <Typography 
                variant="body1" 
                sx={{ 
                  fontSize: { xs: '0.8rem', sm: '1rem', md: '1rem' },
                  color: { xs: 'white', sm: 'var(--primary-color)' },
                  fontFamily: 'Comfortaa',
                  fontWeight: { xs: 'normal', sm: 'normal' },
                  lineHeight: 1.6,
                  textAlign: 'left'
                }}
              >
                {t('home.presentationRight')}
              </Typography>
            </Box>
          </Box>
          
          {/* Bouton Essayez - Desktop/Tablette (après les deux textes) */}
          <Box 
            sx={{ 
              display: { xs: 'none', sm: 'flex' },
              justifyContent: 'center',
              mt: 5,
              mb: 3
            }}
          >
            <Button
              variant="contained"
              sx={{
                background: 'var(--primary-color)',
                color: 'white',
                border: 'none',
                borderRadius: '30px',
                px: 5,
                py: 2,
                fontSize: '1.1rem',
                fontFamily: 'Comfortaa',
                fontWeight: 'bold',
                textTransform: 'none',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
                '&:hover': {
                  background: 'var(--secondary-color)',
                  transform: 'translateY(-8px)',
                  //boxShadow: '0 6px 20px rgba(0, 255, 174, 0.4)',
                  border: 'none'
                },
                transition: 'all 0.3s ease'
              }}
              onClick={() => {
                const pricingSection = document.getElementById('pricing-section');
                if (pricingSection) {
                  // Détection mobile
                  const isMobile = window.innerWidth < 600;
                  if (isMobile) {
                    // Scroll avec offset pour éviter la navbar
                    const yOffset = -80; // Ajustement pour la navbar mobile
                    const y = pricingSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
                    window.scrollTo({ top: y, behavior: 'smooth' });
                  } else {
                    pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }
              }}
            >
              {t('home.tryButton')}
            </Button>
          </Box>
        </Box>

        {/* Features Section ------------------------------------------------*/}
        <Box sx={{ mb: 12,mt:{ xs: -1, sm: 10, md: 12 } }}>
          <Typography 
            variant="h3" 
            sx={{ 
              textAlign: 'center',
              color: 'var(--light-green)',
              mb: 3,
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
              fontFamily: 'Comfortaa'
            }}
          >
            {t('home.mainFeatures')}
          </Typography>
          
          {/* bloc features---------------------------------------- */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {features.map((feature, index) => (
              <Paper
                key={index}
                elevation={3}
                sx={{
                  p: 4,
                  background: 'var(--Fond-carte)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '1px',
                  width: '100vw',
                  marginLeft: 'calc(-50vw + 50%)',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '100%',
                    background: 'linear-gradient(90deg, rgba(255, 75, 140, 0.1) 0%, rgba(104, 241, 209, 0.1) 100%)',
                    opacity: 0.5,
                    transition: 'opacity 0.3s ease'
                  },
                  '&:hover::before': {
                    opacity: 0.8
                  }
                }}
              >
                {/* bloc colonne (xs) ou row (md)---------------------------------------- */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: 'center',
                  gap: { xs: 0, md: 4 },
                  position: 'relative',
                  zIndex: 1,
                  px: { xs: 0, sm: 2 }
                }}>
                {/* bloc largeur colonne txt--------------------------------------- */}
                  <Box sx={{ 
                    flex: '0 0 30%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2
                  }}>
                    {/* bloc icon--------------------------------------- */}
                    <Box sx={{
                      p: 2,
                      borderRadius: '50%',
                      background: 'rgb(37, 36, 44)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 0
                    }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h4" sx={{ 
                      color: 'black',
                      textAlign: 'center',
                      mb: 0,
                      fontWeight: 600,
                      //textShadow: '0 0 10px rgba(0, 0, 0, 0.42)'
                    }}>
                      {feature.title}
                    </Typography>
                    <Typography sx={{ 
                      color: 'black',
                      textAlign: 'center',
                      mb: 2,
                      lineHeight: 1.6
                    }}>
                      {feature.description}
                    </Typography>
                  </Box>

                  <Box sx={{ 
                    flex: { xs: '1 1 auto', md: '1 1 60%' },
                    position: 'relative',
                    height: { xs: '350px', sm: '450px' },
                    //width: { xs: '110%' },
                    background: 'linear-gradient(45deg, rgba(255, 0, 93, 0.2), rgba(104, 241, 209, 0.2))',
                    borderRadius: '20px',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    //boxShadow: 'inset 0 0px 12px rgba(0, 0, 0, 0.91)',
                    mx: { xs: 0 },
                    mb: -1,
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      boxShadow: 'inset 0 0 22px rgba(0, 0, 0, 0.91)',
                      borderRadius: '20px',
                      pointerEvents: 'none',
                      zIndex: 1
                    }
                  }}>
                    {feature.title === t('home.features.statistics.title') && (
                      <>
                        <Box
                          key={`stat-${currentStatImageIndex}-${statSlideDirection}`}
                          component="img"
                          src={statImages[currentStatImageIndex]}
                          alt={`Statistics view ${currentStatImageIndex + 1}`}
                          sx={{
                            ...sharedCarouselImageSx,
                            transition: 'opacity 0.5s ease-in-out',
                            ...slideAnimationStyles[statSlideDirection]
                          }}
                        />
                        <Box sx={{
                          position: 'absolute',
                          bottom: 16,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          zIndex: 2
                        }}>
                          <IconButton
                            onClick={prevStatImage}
                            sx={{
                              color: 'white',
                              bgcolor: 'rgba(0, 0, 0, 0.5)',
                              '&:hover': {
                                bgcolor: 'rgba(0, 0, 0, 0.7)'
                              }
                            }}
                          >
                            <MdArrowBack />
                          </IconButton>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              px: 1,
                              py: 0.5,
                              borderRadius: '999px',
                              backgroundColor: 'rgba(0, 0, 0, 0.35)',
                              backdropFilter: 'blur(6px)'
                            }}
                          >
                            {statImages.map((image, index) => (
                              <Box
                                key={image}
                                sx={{
                                  width: currentStatImageIndex === index ? 18 : 12,
                                  height: 12,
                                  borderRadius: 6,
                                  background: currentStatImageIndex === index
                                    ? 'linear-gradient(90deg, #68f1d1 0%, #6a5cff 100%)'
                                    : 'rgba(255, 255, 255, 0.3)',
                                  transition: 'all 0.3s ease'
                                }}
                              />
                            ))}
                          </Box>
                          <IconButton
                            onClick={nextStatImage}
                            sx={{
                              color: 'white',
                              bgcolor: 'rgba(0, 0, 0, 0.5)',
                              '&:hover': {
                                bgcolor: 'rgba(0, 0, 0, 0.7)'
                              }
                            }}
                          >
                            <MdArrowForward />
                          </IconButton>
                        </Box>
                      </>
                    )}
                    {feature.title === t('home.features.dreamJournal.title') && (
                      <>
                        <Box
                          key={`dream-${currentDreamImageIndex}-${dreamSlideDirection}`}
                          component="img"
                          src={dreamImages[currentDreamImageIndex]}
                          alt={`Dream Journal view ${currentDreamImageIndex + 1}`}
                          sx={{
                            ...sharedCarouselImageSx,
                            transition: 'opacity 0.5s ease-in-out',
                            ...slideAnimationStyles[dreamSlideDirection]
                          }}
                        />
                        <Box sx={{
                          position: 'absolute',
                          bottom: 16,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          zIndex: 2
                        }}>
                          <IconButton
                            onClick={prevDreamImage}
                            sx={{
                              color: 'white',
                              bgcolor: 'rgba(0, 0, 0, 0.5)',
                              '&:hover': {
                                bgcolor: 'rgba(0, 0, 0, 0.7)'
                              }
                            }}
                          >
                            <MdArrowBack />
                          </IconButton>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              px: 1,
                              py: 0.5,
                              borderRadius: '999px',
                              backgroundColor: 'rgba(0, 0, 0, 0.35)',
                              backdropFilter: 'blur(6px)'
                            }}
                          >
                            {dreamImages.map((image, index) => (
                              <Box
                                key={image}
                                sx={{
                                  width: currentDreamImageIndex === index ? 18 : 12,
                                  height: 12,
                                  borderRadius: 6,
                                  background: currentDreamImageIndex === index
                                    ? 'linear-gradient(90deg, #68f1d1 0%, #6a5cff 100%)'
                                    : 'rgba(255, 255, 255, 0.3)',
                                  transition: 'all 0.3s ease'
                                }}
                              />
                            ))}
                          </Box>
                          <IconButton
                            onClick={nextDreamImage}
                            sx={{
                              color: 'white',
                              bgcolor: 'rgba(0, 0, 0, 0.5)',
                              '&:hover': {
                                bgcolor: 'rgba(0, 0, 0, 0.7)'
                              }
                            }}
                          >
                            <MdArrowForward />
                          </IconButton>
                        </Box>
                      </>
                    )}
                    {feature.title === t('home.features.newDream.title') && (
                      <>
                        <Box
                          key={`new-${currentNewDreamImageIndex}-${newDreamSlideDirection}`}
                          component="img"
                          src={newDreamImages[currentNewDreamImageIndex]}
                          alt={`New Dream view ${currentNewDreamImageIndex + 1}`}
                          sx={{
                            ...sharedCarouselImageSx,
                            transition: 'opacity 0.5s ease-in-out',
                            ...slideAnimationStyles[newDreamSlideDirection]
                          }}
                        />
                        <Box sx={{
                          position: 'absolute',
                          bottom: 16,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          zIndex: 2
                        }}>
                          <IconButton
                            onClick={prevNewDreamImage}
                            sx={{
                              color: 'white',
                              bgcolor: 'rgba(0, 0, 0, 0.5)',
                              '&:hover': {
                                bgcolor: 'rgba(0, 0, 0, 0.7)'
                              }
                            }}
                          >
                            <MdArrowBack />
                          </IconButton>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              px: 1,
                              py: 0.5,
                              borderRadius: '999px',
                              backgroundColor: 'rgba(0, 0, 0, 0.35)',
                              backdropFilter: 'blur(6px)'
                            }}
                          >
                            {newDreamImages.map((image, index) => (
                              <Box
                                key={image}
                                sx={{
                                  width: currentNewDreamImageIndex === index ? 18 : 12,
                                  height: 12,
                                  borderRadius: 6,
                                  background: currentNewDreamImageIndex === index
                                    ? 'linear-gradient(90deg, #68f1d1 0%, #6a5cff 100%)'
                                    : 'rgba(255, 255, 255, 0.3)',
                                  transition: 'all 0.3s ease'
                                }}
                              />
                            ))}
                          </Box>
                          <IconButton
                            onClick={nextNewDreamImage}
                            sx={{
                              color: 'white',
                              bgcolor: 'rgba(0, 0, 0, 0.5)',
                              '&:hover': {
                                bgcolor: 'rgba(0, 0, 0, 0.7)'
                              }
                            }}
                          >
                            <MdArrowForward />
                          </IconButton>
                        </Box>
                      </>
                    )}
                    {feature.title === t('home.features.gallery.title') && (
                      <>
                        <Box
                          sx={{
                            position: 'relative',
                            width: '100%',
                            height: '100%',
                            //borderRadius: '32px',
                            overflow: 'hidden',
                            //boxShadow: '0 12px 30px rgba(0, 0, 0, 0.45)'
                          }}
                        >
                          <Box
                            key={`gallery-${currentGalleryImageIndex}-${gallerySlideDirection}`}
                            component="img"
                            src={dreamGalleryImages[currentGalleryImageIndex]}
                            alt={`Galerie des rêves ${currentGalleryImageIndex + 1}`}
                            sx={{
                              ...sharedCarouselImageSx,
                              transition: 'opacity 0.5s ease-in-out',
                              ...slideAnimationStyles[gallerySlideDirection]
                            }}
                          />
                          <Box sx={{
                            position: 'absolute',
                            bottom: 16,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            zIndex: 2
                          }}>
                            <IconButton
                              onClick={prevGalleryImage}
                              sx={{
                                color: 'white',
                                bgcolor: 'rgba(0, 0, 0, 0.5)',
                                '&:hover': {
                                  bgcolor: 'rgba(0, 0, 0, 0.7)'
                                }
                              }}
                            >
                              <MdArrowBack />
                            </IconButton>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                px: 1,
                                py: 0.5,
                                borderRadius: '999px',
                                backgroundColor: 'rgba(0, 0, 0, 0.35)',
                                backdropFilter: 'blur(6px)'
                              }}
                            >
                              {dreamGalleryImages.map((image, index) => (
                                <Box
                                  key={image}
                                  sx={{
                                    width: currentGalleryImageIndex === index ? 18 : 12,
                                    height: 12,
                                    borderRadius: 6,
                                    background: currentGalleryImageIndex === index
                                      ? 'linear-gradient(90deg, #68f1d1 0%, #6a5cff 100%)'
                                      : 'rgba(255, 255, 255, 0.3)',
                                    transition: 'all 0.3s ease'
                                  }}
                                />
                              ))}
                            </Box>
                            <IconButton
                              onClick={nextGalleryImage}
                              sx={{
                                color: 'white',
                                bgcolor: 'rgba(0, 0, 0, 0.5)',
                                '&:hover': {
                                  bgcolor: 'rgba(0, 0, 0, 0.7)'
                                }
                              }}
                            >
                              <MdArrowForward />
                            </IconButton>
                          </Box>
                        </Box>
                      </>
                    )}
                    {feature.title !== t('home.features.statistics.title') &&
                     feature.title !== t('home.features.dreamJournal.title') &&
                     feature.title !== t('home.features.newDream.title') &&
                     feature.title !== t('home.features.gallery.title') && (
                      <Typography sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        textAlign: 'center'
                      }}>
                        Carrousel d'images à venir
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>

        
        {/* Testimonials Section ------------------------------------------------*/}
        <Box sx={{ mb: 12, mt:{ xs: -1, sm: 10, md: 12 } }}>
          <Typography 
            variant="h3" 
            sx={{ 
              textAlign: 'center',
              color: 'var(--light-green)',
              mb: 1,
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
              fontFamily: 'Comfortaa'
            }}
          >
            {t('home.testimonials.title')}
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} sm={6} md={4} key={index} sx={{ mr: -1, ml: -1 }}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    background: 'var(--testimonials)',
                    //boxShadow: '0 0 10px rgba(18, 74, 207, 0.91)',
                    //backdropFilter: 'blur(10px)',
                    //border: '0px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '10px',
                    p: 1,
                    mb: -3                                       
                  }}
                >
                  <CardContent>
                    <Typography sx={{ color: 'black', mb: 1, fontSize: '0.9rem' }}>
                      "{testimonial.comment}"
                    </Typography>
                    <Box sx={{ mt: 'auto' }}>
                      <Typography sx={{ color: 'black', fontWeight: 600 }}>
                        {testimonial.name}
                      </Typography>
                      <Rating
                        value={testimonial.rating}
                        readOnly
                        icon={<StarIcon sx={{ color: '#ffdd1f' }} />}
                        emptyIcon={<StarIcon sx={{ opacity: 0.55, color: '#68f1d1' }} />}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>


        {/* Pricing Section ------------------------------------------------*/}
        <Box id="pricing-section" >
          <Typography 
            variant="h3" 
            sx={{ 
              textAlign: 'center',
              color: 'var(--light-green)',
              mb: 0,
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
              fontFamily: 'Comfortaa'
            }}
          >
            {t('home.pricing.title')}
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              textAlign: 'center',
              color: 'white',
              mb: { xs: -3, sm: 0, md: 0 },
              fontFamily: 'Comfortaa'
            }}
          >
            {t('home.pricing.subtitle')}
          </Typography>
          <Container maxWidth="lg" sx={{ py: 4 }}>            
            <Grid container spacing={4}>
              {/* Gratuit ------------------------------------------------------------*/}
              <Grid item xs={12} md={4}>
                <Paper 
                  onClick={() => handlePricingButtonClick(null)}
                  sx={{ 
                    p: 3,
                    height: '100%',
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '40px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                    transition: 'transform 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.95)',
                    }
                  }}>
                  <Typography variant="h4" sx={{ mb: 2 }}>
                    {t('home.pricing.free.title')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
                    <Typography variant="h2" sx={{ color: 'var(--light-green)' }}>
                      0€
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.7)', ml: 1 }}>
                      
                    </Typography>
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography sx={{ mb: 0 }}>✓ {t('home.pricing.features.free.feature1')}</Typography>
                    <Typography sx={{ mb: 0 }}>✓ {t('home.pricing.features.free.feature2')}</Typography>
                    <Typography sx={{ mb: 0 }}>✓ {t('home.pricing.features.free.feature3')}</Typography>
                    <Typography sx={{ mb: 0 }}>✓ {t('home.pricing.features.free.feature4')}</Typography>
                    <Typography sx={{ mb: 0 }}>✓ {t('home.pricing.features.free.feature5')}</Typography>
                    <Typography sx={{ mb: 3 }}>✓ {t('home.pricing.features.free.feature6')}</Typography>
                  </Box>
                  {/*
                  <Button 
                    fullWidth 
                    onClick={() => handlePricingButtonClick(null)}
                    sx={{
                      background: 'var(--card-gradient)',
                      color: 'white',
                      padding: '12px 30px',
                      borderRadius: '25px',
                      textTransform: 'none',
                      fontWeight: 500,
                      boxShadow: '0 8px 15px rgba(0, 0, 0, 0.4)',
                      fontSize: '1rem',
                      '&:hover': {
                        background: 'var(--button-gradient)',
                      }
                    }}
                  >
                    {t('home.pricing.startFree')}
                  </Button>
                  */}
                </Paper>
              </Grid>

              {/* BASIC ------------------------------------------------*/}
              <Grid item xs={12} md={4}>
                <Paper 
                  onClick={() => handlePricingButtonClick('price_1ThSiHHxwALmXf4xJUzGzHZE')}
                  sx={{ 
                    p: 3,
                    height: '100%',
                    background: 'rgba(104, 241, 209, 0.3)',
                    borderRadius: '40px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                    transition: 'transform 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.95)',
                    }
                  }}>
                  <Typography variant="h4" sx={{ mb: 2 }}>
                    {t('home.pricing.basic.title')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
                    <Typography variant="h2" sx={{ color: '#68f1d1' }}>
                      5€
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.7)', ml: 1 }}>
                      {t('home.pricing.perMonth')}
                    </Typography>
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography sx={{ mb: 0 }}>✓ {t('home.pricing.features.basic.feature1')}</Typography>
                    <Typography sx={{ mb: 0 }}>✓ {t('home.pricing.features.basic.feature2')}</Typography>
                    <Typography sx={{ mb: 0 }}>✓ {t('home.pricing.features.basic.feature3')}</Typography>
                    <Typography sx={{ mb: 0 }}>✓ {t('home.pricing.features.basic.feature4')}</Typography>
                    <Typography sx={{ mb: 0 }}>✓ {t('home.pricing.features.basic.feature5')}</Typography>
                    <Typography sx={{ mb: 3 }}>✓ {t('home.pricing.features.basic.feature6')}</Typography>
                  </Box>
                  {/*
                  <Button 
                    fullWidth 
                    onClick={() => handlePricingButtonClick('price_1QmypVH8wRmT8rZfi74sICmZ')}
                    sx={{
                      background: 'var(--card-gradient)',
                      color: 'white',
                      padding: '12px 30px',
                      borderRadius: '25px',
                      textTransform: 'none',
                      fontWeight: 500,
                      boxShadow: '0 8px 15px rgba(0, 0, 0, 0.4)',
                      fontSize: '1rem',
                      '&:hover': {
                        background: 'var(--button-gradient)',
                      }
                    }}
                  >
                    {t('home.pricing.chooseBasic')}
                  </Button>
                  */}
                </Paper>
              </Grid>

              {/* PREMIUM ------------------------------------------------*/}
              <Grid item xs={12} md={4}>
                <Paper 
                  onClick={() => handlePricingButtonClick('price_1ThSqeHxwALmXf4xuBSnhDcL')}

                  sx={{
                    p: 3,
                    height: '100%',
                    background: 'var(--Pages-background)',
                    color: 'white',
                    position: 'relative',
                    transition: 'transform 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.95)',
                    }
                  }}>
                  <Box 
                    sx={{ 
                      position: 'absolute',
                      top: -12,
                      right: '10%',
                      background: 'var(--star-iconsDeg)',                      
                      p: 1,
                      borderRadius: '25px',
                      color: 'black',
                      fontWeight: 'bold',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                    }}
                  >
                    {t('home.pricing.mostPopular')}
                  </Box>
                  <Typography variant="h4" sx={{ mb: 1, color: 'white' }}>
                    {t('home.pricing.premium.title')}
                  </Typography>
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                      <Typography variant="h2" sx={{ color: 'var(--light-green)' }}>
                        10€
                      </Typography>
                      <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.9) !important', ml: 1 }}>
                        {t('home.pricing.perMonth')}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'baseline', mt: 1 }}>
                      <Typography variant="h2" sx={{ color: 'var(--light-green)' }}>
                        110€
                      </Typography>
                      <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.9) !important', ml: 1 }}>
                        {t('home.pricing.perYear')}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography sx={{ mb: 0, color: 'white !important' }}>✓ {t('home.pricing.features.premium.feature1')}</Typography>
                    <Typography sx={{ mb: 0, color: 'white !important' }}>✓ {t('home.pricing.features.premium.feature2')}</Typography>
                    <Typography sx={{ mb: 0, color: 'white !important' }}>✓ {t('home.pricing.features.premium.feature3')}</Typography>
                    <Typography sx={{ mb: 0, color: 'white !important' }}>✓ {t('home.pricing.features.premium.feature4')}</Typography>
                    <Typography sx={{ mb: 0, color: 'white !important' }}>✓ {t('home.pricing.features.premium.feature5')}</Typography>
                    <Typography sx={{ mb: 0, color: 'white !important' }}>✓ {t('home.pricing.features.premium.feature6')}</Typography>
                    <Typography sx={{ mb: 3, color: 'white !important' }}>✓ {t('home.pricing.features.premium.feature7')}</Typography>
                  </Box>
                  <Stack spacing={2}>
                    <Button 
                      fullWidth 
                      onClick={() => handlePricingButtonClick('price_1ThSqeHxwALmXf4xuBSnhDcL')}
                      sx={{
                        background: 'var(--card-gradient)',
                        color: 'white',
                        padding: '12px 30px',
                        borderRadius: '25px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '1rem',
                        '&:hover': {
                          color: 'black',
                          background: 'rgba(255,255,255,0.9)',
                        }
                      }}
                    >
                      {t('profile.changePlan.monthlyPremium')}
                    </Button>

                    <Button 
                      fullWidth 
                      onClick={() => handlePricingButtonClick('price_1ThSsRHxwALmXf4x3yyg0nbr')}
                      sx={{
                        background: 'var(--card-gradient)',
                        color: 'white',
                        padding: '12px 30px',
                        borderRadius: '25px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '1rem',
                        '&:hover': {
                          color: 'black',
                          background: 'rgba(255,255,255,0.9)',
                        }
                      }}
                    >
                      {t('profile.changePlan.yearlyPremium')}
                    </Button>
                  </Stack>
                </Paper>
              </Grid>

            </Grid>
          </Container>
        </Box>
      </Container>
      <Dialog 
        open={showLoginWarning} 
        onClose={() => setShowLoginWarning(false)}
        PaperProps={{
          sx: {
            background: 'var(--fond-Dialog)',
            borderRadius: '20px',
          }
        }}
      >
        <DialogTitle sx={{ color: 'white', textAlign: 'center' }}>
          {t('home.pricing.loginRequired')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'white' }}>
            {t('home.pricing.loginRequiredMessage')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, alignItems: 'center', justifyContent: 'center' }}>
          <Button 
            onClick={() => setShowLoginWarning(false)}
            sx={{
              background: 'linear-gradient(90deg, #FF4B8C 50%, #FF8E6E 100%)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(90deg, #FF4B8C 10%, #68f1d1 150%)',
              }
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={() => {
              setShowLoginWarning(false);
              openLoginModal();
            }}
            sx={{
              background: 'linear-gradient(90deg, #FF4B8C 50%, #FF8E6E 100%)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(90deg, #FF4B8C 10%, #68f1d1 150%)',
              }
            }}
          >
            {t('common.continue')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </>
  );
}